import type { RefObject } from "react";

type SelectedGuest = {
  id: number;
} | null;

type MapModalProps = {
  open: boolean;
  locationError: string;
  mapsUnavailableMessage?: string;
  selectedGuest: SelectedGuest;
  mapRef: RefObject<HTMLDivElement | null>;
  onUseCurrent: () => void;
  onClose: () => void;
  title: string;
  hint: string;
  useCurrentLabel: string;
  closeLabel: string;
};

export default function MapModal({
  open,
  locationError,
  mapsUnavailableMessage,
  mapRef,
  onUseCurrent,
  onClose,
  title,
  hint,
  useCurrentLabel,
  closeLabel,
}: MapModalProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "var(--modal-backdrop)" }}
    >
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] shadow-[var(--modal-shadow)]">
        <div className="flex flex-col gap-3 border-b border-[color:var(--divider)] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <p className="text-sm font-semibold text-[color:var(--foreground)]">{title}</p>
            <p className="text-xs text-[color:var(--muted)]">{hint}</p>
            {mapsUnavailableMessage ? (
              <p className="mt-1 text-xs text-amber-500">{mapsUnavailableMessage}</p>
            ) : null}
            {locationError ? <p className="mt-1 text-xs text-rose-400">{locationError}</p> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onUseCurrent}
              data-tab-trigger="true"
              className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
            >
              {useCurrentLabel}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-tab-trigger="true"
              className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
            >
              {closeLabel}
            </button>
          </div>
        </div>
        <div className="h-[52svh] w-full sm:h-[60vh]">
          {mapsUnavailableMessage ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-[color:var(--muted)]">
              {mapsUnavailableMessage}
            </div>
          ) : (
            <div ref={mapRef} className="h-full w-full" />
          )}
        </div>
      </div>
    </div>
  );
}
